// @flow
import * as React from "react"
import { Redirect } from "react-router-dom"
import { sum, groupWith, equals } from "ramda"
import { compose, withProps, withHandlers, type HOC } from "recompose"
import config from "global/config"
import withUser from "hoc/withUser"
import withConnect from "hoc/withConnect"
import withRouter from "hoc/withRouter"
import { selectAppConfig } from "data/app/selectors"
import { selectDates, selectOriginalReservation, selectAccessories, selectShippingAddress } from "data/reservations/selectors"
import { pickAccessories, setShippingAddress } from "data/reservations/actions"
import type { ProductVariant, ProductVariantAvailableAffiliate } from "data/product/types"
import { clear } from "data/search/actions"
import { getPricePerDayArray } from "data/units/money/helpers"
import urls from "data/router/urls"
import { openModal } from "data/modals/actions"
import Button from "components/Button"
import ProductPreview from "components/ProductPreview"
import ReservationPricing from "components/ReservationPricing"
import { Text } from "componentsStyled/Typography/Texts"
import { Wrap, WrapGrouper } from "componentsStyled/Layout/Wrap"
import { VPaddedFullWidthContainer } from "componentsStyled/Layout/Containers"
import { StyledInput, StyledTextarea } from "componentsStyled/Forms/Input"
import PlaceSearch from "pages/AffiliateRegistration/AffiliateSelector/PlaceSearch"
import SignUpModal from "modals/SignUp"
import Accessories from "./Accessories"
import Dates from "./Dates"

const CartForm = ({
  appConfig,
  productVariant,
  dates,
  variantAffiliate,
  handleContinue,
  loading,
  user,
  originalReservation,
  pickAccessories,
  accessories, // The list of chosen accessories by the user
  shippingAddress,
  onChangeShippingAddress,
  openModal,
}) => {
  if (!dates) {
    return <Redirect to={urls.products} />
  }

  const currency = variantAffiliate.location.country.currency

  const pricingInput = {
    productVariantId: productVariant.id,
    affiliateId: variantAffiliate.affiliateId,
    start: dates.startDate,
    end: dates.endDate,
    accessories: accessories && accessories.map(a => ({ name: a.name })),
  }

  const availability = {
    product: productVariant,
    user: user,
    selectedDates: {
      startDate: dates.startDate,
      endDate: dates.endDate,
    },
    affiliate: variantAffiliate.name,
  }

  const productGroup = productVariant.product.productGroup

  return (
    <VPaddedFullWidthContainer narrow>
      <WrapGrouper>
        <Text uppercase bold black>
          Booking details
        </Text>
        <Wrap>
          <Dates dates={dates} originalReservation={originalReservation} />
        </Wrap>
        <Wrap>
          <ProductPreview
            productVariant={productVariant}
            affiliateName={variantAffiliate.name}
          />
        </Wrap>
        <Accessories
          handleAccessoriesSelection={pickAccessories}
          pricingInput={pricingInput}
          currency={currency}
          originalReservation={originalReservation}
        />
      </WrapGrouper>
      {appConfig && appConfig.enableShipping &&
        <WrapGrouper>
          <Text uppercase bold black>
            Shipping address
          </Text>
          <PlaceSearch
            handleChange={onChangeShippingAddress}
            value={shippingAddress || ''}
            debounce={1000}
            types={["address"]}
            placeholder={"Start searching to find your address..."}
          />
        </WrapGrouper>
      }
      <WrapGrouper>
        <Text uppercase bold black>
          Total
        </Text>
        <Wrap>
          <ReservationPricing
            input={pricingInput}
            originalReservation={originalReservation}
            availability={availability}
            productGroupName={productGroup.name}
            user={user}
          />
        </Wrap>
      </WrapGrouper>
      <Button
        fullwidth
        onClick={handleContinue}
        loading={loading}
        disabled={loading}
      >
        Continue to checkout
      </Button>
    </VPaddedFullWidthContainer>
  )
}

const mapStateToProps = state => ({
  appConfig: selectAppConfig(state),
  dates: selectDates(state),
  originalReservation: selectOriginalReservation(state),
  accessories: selectAccessories(state),
  shippingAddress: selectShippingAddress(state),
})

type Outter = {|
  productVariant: ProductVariant,
  variantAffiliate: ProductVariantAvailableAffiliate,
|}

const mapDispatchToProps = {
  openModal,
  pickAccessories,
  setShippingAddress,
  clear,
}

const enhancer: HOC<*, Outter> = compose(
  withUser(),
  withConnect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withProps(({ dates, variantAffiliate }) => {
    const prices = getPricePerDayArray(dates, variantAffiliate)
    return {
      priceGroups: groupWith(equals, prices),
      total: sum(prices),
    }
  }),
  withHandlers({
    onChangeShippingAddress: props => (value) => {
      props.setShippingAddress(value)
    },
    handleContinue: props => () => {
      const goToReservePage = () => props.history.push(
        urls.reserveProduct(
          props.productVariant.id,
          props.variantAffiliate.affiliateId,
        ),
      )

      return !props.user
        ? props.openModal(SignUpModal, { callback: () => goToReservePage() })
        : goToReservePage()
    }
  }),
)

export default enhancer(CartForm)



// WEBPACK FOOTER //
// ./src/pages/Cart/CartForm/index.js